const lazy_carousels = new WeakSet();
let lazy_carousel_id = 0;
let swiper_script_promise = null;

function ensureSwiperScript() {
  if (typeof window.Swiper !== 'undefined') {
    return Promise.resolve();
  }

  if (swiper_script_promise) {
    return swiper_script_promise;
  }

  swiper_script_promise = new Promise((resolve, reject) => {
    let script = document.querySelector('[data-irroba-swiper-script], script[src*="swiper-bundle11.min.js"]');

    if (!script) {
      script = document.createElement('script');
      script.src = 'https://cdn.irroba.com.br/static/catalog/js/swiper/swiper-bundle11.min.js';
      script.defer = true;
      script.dataset.irrobaSwiperScript = 'true';
    }

    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });

    if (!script.isConnected) {
      document.head.appendChild(script);
    }
  });

  return swiper_script_promise;
}

function loadSwiperAndInitialize(initializer) {
  ensureSwiperScript()
    .then(initializer)
    .catch((error) => console.error('Erro ao carregar o Swiper:', error));
}

function scheduleProductHeightCalculation() {
  if (typeof window.calculateProductHeight !== 'function') {
    return;
  }

  window.requestAnimationFrame(() => window.calculateProductHeight());
}

function deferCarouselInitialization(element, initializer) {
  if (
    !element.hasAttribute('data-swiper-lazy') ||
    element.dataset.swiperLazyReady === 'true' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return false;
  }

  if (lazy_carousels.has(element)) {
    return true;
  }

  const carousel_id = `swiper-lazy-${++lazy_carousel_id}`;
  element.dataset.swiperLazyId = carousel_id;
  lazy_carousels.add(element);

  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) {
      return;
    }

    observer.disconnect();
    element.dataset.swiperLazyReady = 'true';

    requestAnimationFrame(() => {
      initializer(`[data-swiper-lazy-id="${carousel_id}"]`);
    });
  }, {
    rootMargin: '300px 0px'
  });

  observer.observe(element);

  return true;
}

export function initPageCarousels(
  selector,
  globalOverrides = {},
  forceReinit = false,
) {
  if (selector) {
    initDataAttrCarousels(selector, globalOverrides, forceReinit);
    return;
  }

  initDefaultCarousels();
  initDataAttrCarousels(undefined, globalOverrides, forceReinit);
}

function initDefaultCarousels(
  selector = '[data-swiper]:not([data-swiper="true"]):not([data-swiper="false"])',
) {
  const carousels = document.querySelectorAll(selector);

  if (carousels.length === 0) {
    console.log("ℹ️ Nenhum carousel encontrado para o seletor:", selector);
    return;
  }

  const carouselInstances = new Map();

  carousels.forEach((el) => {
    if (deferCarouselInitialization(el, (lazy_selector) => initDefaultCarousels(lazy_selector))) {
      return;
    }

    const swiperConfig = (el.dataset.swiper || "").trim();

    if (swiperConfig === "true") return;

    // Evita reinicialização (idempotente entre chamadas)
    if (el.swiper) return;

    if (document.body.classList.contains('theme-_lime') && el.querySelectorAll('.swiper-slide').length <= 1) {
      el.classList.add('swiper--few-slides');
      return;
    }

    if (typeof window.Swiper === 'undefined') {
      loadSwiperAndInitialize(() => initDefaultCarousels(selector));
      return;
    }

    const rawOptions = swiperConfig
      ? JSON.parse(swiperConfig)
      : {};

    const swiperOptions = {
      ...rawOptions
    };

    if (rawOptions.navigation === true) {
      swiperOptions.navigation = getSwiperNavigation(el, rawOptions.navigationScope);
    }

    if (rawOptions.pagination === true) {
      const paginationType = rawOptions.paginationType || 'bullets';
      const paginationConfig = {
        el: el.querySelector('.swiper-pagination'),
        clickable: true
      };

      if (paginationType === 'dynamic') {
        paginationConfig.dynamicBullets = true;
      } else if (paginationType !== 'bullets' && paginationType !== 'default') {
        paginationConfig.type = paginationType;
      }

      swiperOptions.pagination = paginationConfig;
    }

    if (rawOptions.autoplay === true) {
      swiperOptions.autoplay = {
        delay: Number(rawOptions.autoplayDelay) || 4000,
        disableOnInteraction: false
      };
    }

    delete swiperOptions.navigationScope;
    delete swiperOptions.paginationType;
    delete swiperOptions.autoplayMobile;

    el.swiper = new Swiper(el, swiperOptions);

    updateFewSlidesClass(el);
    scheduleProductHeightCalculation();

    const carouselId = el.dataset.carouselId;
    exposeCarousel(carouselId, carouselInstances, el);
  });

  // 🔗 Sincronização de carousels (thumbs) — após todas as instâncias criadas
  setupCarouselSync(carouselInstances);
}

function getSwiperNavigation(el, navigationScope) {
  let searchScope = el;

  if (navigationScope) {
    const scopeElement = document.querySelector(navigationScope);

    if (scopeElement) {
      searchScope = scopeElement;
    } else {
      console.warn("⚠️ Escopo de navegação não encontrado:", navigationScope);
    }
  }

  const nextEl = searchScope.querySelector('.swiper-button-next');
  const prevEl = searchScope.querySelector('.swiper-button-prev');

  if (!nextEl || !prevEl) {
    return false;
  }

  return {
    nextEl,
    prevEl
  };
}

function initDataAttrCarousels(
  selector = '[data-swiper="true"]',
  globalOverrides = {},
  forceReinit = false,
) {
  const carousels = document.querySelectorAll(selector);
  if (carousels.length === 0) {
    console.log("ℹ️ Nenhum carousel encontrado para o seletor:", selector);
    return;
  }
  // 🔗 Armazena instâncias para sincronização posterior
  const carouselInstances = new Map();

  carousels.forEach((el) => {
    if (deferCarouselInitialization(el, (lazy_selector) => initDataAttrCarousels(lazy_selector, globalOverrides, forceReinit))) {
      return;
    }

    const swiperConfig = (el.dataset.swiper || "").trim();

    if (swiperConfig && swiperConfig !== "true") return;

    const totalSlides = el.querySelectorAll(".swiper-slide").length;

    // ⚙️ Reinicialização forçada
    if (forceReinit && el.swiper) {
      try {
        el.swiper.destroy(true, true);
        delete el.swiper;
      } catch (err) {
        console.warn("Erro ao destruir Swiper anterior:", err);
      }
    }
    // Evita reinicialização desnecessária
    if (el.swiper) return;

    // 🧩 Configurações padrão
    if (document.body.classList.contains('theme-_lime') && totalSlides <= 1) {
      el.classList.add('swiper--few-slides');
      return;
    }

    if (typeof window.Swiper === 'undefined') {
      loadSwiperAndInitialize(() => initDataAttrCarousels(selector, globalOverrides, forceReinit));
      return;
    }

    const defaultOptions = {
      loop: true,
      rewind: false,
      spaceBetween: 16,
      slidesPerView: 4,
      freeMode: false,
      watchOverflow: true,
      breakpoints: {
        320: { slidesPerView: 1, spaceBetween: 8 },
        640: { slidesPerView: 2, spaceBetween: 12 },
        1024: { slidesPerView: 3, spaceBetween: 16 },
        1440: { slidesPerView: 4, spaceBetween: 20 },
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
    };

    // 🧩 Configurações via dataset
    const datasetOptions = {
      loop: el.dataset.loop === "true",
      rewind: el.dataset.rewind === "true",
      freeMode: el.dataset.freeMode === "true",
      spaceBetween: Number(
        el.dataset.spaceBetween || defaultOptions.spaceBetween,
      ),
      slidesPerView: Number(
        el.dataset.slidesPerView || defaultOptions.slidesPerView,
      ),
    };

    // 🧩 Configurações de Pagination (Dots)
    if (el.dataset.pagination === "true" || el.dataset.dots === "1") {
      const paginationType = el.dataset.paginationType || "default";
      const paginationConfig = {
        el: el.querySelector(".swiper-pagination"),
        clickable: true,
      };

      switch (paginationType) {
        case "progressbar":
          paginationConfig.type = "progressbar";
          break;
        case "dynamic":
          paginationConfig.dynamicBullets = true;
          break;
        case "default":
          break;
        default:
          paginationConfig.type = paginationType;
      }

      datasetOptions.pagination = paginationConfig;
    }

    // 🧩 Configurações de Autoplay
    if (el.dataset.autoplay === "true" || el.dataset.carouselAutoplay === "1") {
      const autoplayDelay = Number(el.dataset.autoplayDelay) || 4000;
      const disableOnInteraction = el.dataset.autoplayDisable !== "false";

      // Verificar se deve desabilitar em mobile
      const disableOnMobile =
        el.dataset.autoplayMobile === "false" && window.innerWidth < 992;

      datasetOptions.autoplay = disableOnMobile
        ? false
        : {
          delay: autoplayDelay,
          disableOnInteraction: disableOnInteraction,
        };
    }

    // 🧩 Configurações de Lazy Loading
    if (el.dataset.lazy === "true") {
      datasetOptions.lazy = true;
      datasetOptions.preloadImages = false;
      datasetOptions.watchSlidesProgress = true;
    }

    // 🧩 Configurações de Zoom (para carousel de produto)
    if (el.dataset.zoom === "true") {
      datasetOptions.zoom = {
        maxRatio: Number(el.dataset.zoomMaxRatio) || 3,
        minRatio: Number(el.dataset.zoomMinRatio) || 1,
        toggle: el.dataset.zoomToggle !== "false",
      };
    }

    // 🧩 Configurações de Direction (horizontal/vertical)
    if (el.dataset.direction) {
      datasetOptions.direction = el.dataset.direction;
    }

    // 🧩 Configurações especiais para watch
    if (el.dataset.watchSlidesProgress === "true") {
      datasetOptions.watchSlidesProgress = true;
    }
    if (el.dataset.watchSlidesVisibility === "true") {
      datasetOptions.watchSlidesVisibility = true;
    }
    if (el.dataset.watchOverflow === "true") {
      datasetOptions.watchOverflow = true;
    }

    // 🧩 Configurações de centeredSlides
    if (el.dataset.centeredSlides === "true") {
      datasetOptions.centeredSlides = true;
    } else if (el.dataset.centeredSlides === "false") {
      datasetOptions.centeredSlides = false;
    }

    // 🧩 Configurações de Navegação (Setas)
    if (el.dataset.navigation === "false" || el.dataset.nav === "0") {
      // Desabilita explicitamente a navegação
      datasetOptions.navigation = false;
    } else if (el.dataset.navigation === "true" || el.dataset.nav === "1") {
      // Define o escopo de busca dos botões
      let searchScope = el;

      // Se existe data-navigation-scope, busca no escopo especificado (ex: container pai)
      if (el.dataset.navigationScope) {
        const scopeElement = document.querySelector(el.dataset.navigationScope);
        if (scopeElement) {
          searchScope = scopeElement;
        } else {
          console.warn(
            "⚠️ Escopo de navegação não encontrado:",
            el.dataset.navigationScope,
          );
        }
      }

      // Busca botões dentro do elemento ou usa seletores padrão
      let nextEl = searchScope.querySelector(".swiper-button-next");
      let prevEl = searchScope.querySelector(".swiper-button-prev");

      // Se não encontrar, tenta buscar como filhos diretos do carousel
      if (!nextEl || !prevEl) {
        nextEl = el.querySelector(".swiper-button-next");
        prevEl = el.querySelector(".swiper-button-prev");
      }

      // Adiciona navigation se os elementos existirem OU usa string seletora padrão
      if (nextEl && prevEl) {
        datasetOptions.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };
      } else {
        // Fallback: usa seletores em string (Swiper vai buscar globalmente)
        datasetOptions.navigation = {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        };
      }
    }

    // 🧩 Parseia breakpoints do dataset (se existirem)
    let datasetBreakpoints = undefined;
    try {
      // Opção 1: JSON no data-breakpoints
      if (el.dataset.breakpoints) {
        datasetBreakpoints = JSON.parse(el.dataset.breakpoints);
        for (const key in datasetBreakpoints) {
          const bp = datasetBreakpoints[key];
          bp.slidesPerView = Number(
            bp.slidesPerView || datasetOptions.slidesPerView,
          );
          bp.spaceBetween = Number(
            bp.spaceBetween || datasetOptions.spaceBetween,
          );
          bp.loop =
            totalSlides > bp.slidesPerView ? datasetOptions.loop : false;
        }
      }
    } catch (e) {
      console.warn("Erro ao ler data-breakpoints:", e);
    }

    // 🧩 Seleciona qual breakpoints usar (sem merge)
    const finalBreakpoints =
      globalOverrides.breakpoints ||
      datasetBreakpoints ||
      defaultOptions.breakpoints;

    // 🧩 Configuração final (prioridade: default < dataset < globalOverrides)
    const swiperOptions = {
      ...defaultOptions,
      ...datasetOptions,
      ...globalOverrides,
      breakpoints: finalBreakpoints,
    };

    // 🧩 Tratamento especial para navigation (pode ser false)
    if (
      datasetOptions.navigation === false ||
      globalOverrides.navigation === false
    ) {
      swiperOptions.navigation = false;
    } else if (datasetOptions.navigation) {
      // Usa a configuração do dataset (já tem nextEl e prevEl configurados)
      swiperOptions.navigation = datasetOptions.navigation;
    } else if (globalOverrides.navigation) {
      // Usa a configuração do globalOverrides
      swiperOptions.navigation = globalOverrides.navigation;
    } else {
      // Usa a configuração padrão
      swiperOptions.navigation = defaultOptions.navigation;
    }

    // Ajusta o loop conforme número de slides
    swiperOptions.loop =
      totalSlides > swiperOptions.slidesPerView ? swiperOptions.loop : false;

    // 🔗 Inicializa o Swiper
    el.swiper = new Swiper(el, swiperOptions);

    updateFewSlidesClass(el);
    scheduleProductHeightCalculation();

    // 🔗 Armazena instância com identificador (para sincronização)
    const carouselId = el.dataset.carouselId;
    exposeCarousel(carouselId, carouselInstances, el);
  });

  // 🔗 Sincronização de carousels (thumbs)
  setupCarouselSync(carouselInstances);
}

// 🏷️ Classe indicativa quando slides são menores que slidesPerView
function updateFewSlidesClass(el) {
  const totalSlides = el.querySelectorAll(".swiper-slide").length;
  const currentSlidesPerView = el.swiper.params.slidesPerView;
  el.classList.toggle("swiper--few-slides", totalSlides < currentSlidesPerView);
}

function exposeCarousel(carouselId, carouselInstances, el) {
  if (carouselId) {
    carouselInstances.set(carouselId, el.swiper);

    // Expõe globalmente para compatibilidade
    if (carouselId === "productMainCarousel") {
      window.productMainCarousel = el.swiper;
      if (typeof $ !== "undefined") $.productMainCarousel = el.swiper;
      setupMainCarouselWholeSlides(el.swiper);
    }
    if (carouselId === "productThumbsCarousel") {
      window.productThumbsCarousel = el.swiper;
      if (typeof $ !== "undefined") $.productThumbsCarousel = el.swiper;
    }
  }
}

/**
 * 🏷️ Marcador `productMainCarousel--wholeSlides`: indica que o slidesPerView
 * atual é inteiro (imagem exibida por inteiro, sem peek). Serve apenas para o
 * CSS aplicar os paddings laterais — NÃO tem `display: contents`, então o
 * carrossel continua normal (deslizável) em qualquer situação.
 *
 * - slidesPerView inteiro (1, 2, ...): aplica a classe → paddings laterais.
 * - slidesPerView quebrado (ex.: 1.3): sem a classe → padding-right 0 (peek).
 *
 * Reavaliado a cada troca de breakpoint/resize.
 */
function setupMainCarouselWholeSlides(swiper) {
  const applyWholeSlides = () => {
    const slidesPerView = parseFloat(swiper.params.slidesPerView);
    const isWhole = Number.isInteger(slidesPerView);
    swiper.el.classList.toggle("productMainCarousel--wholeSlides", isWhole);
  };

  applyWholeSlides();
  swiper.on("breakpoint", applyWholeSlides);
  window.addEventListener("resize", applyWholeSlides);
}

/**
 * 🔗 Configura sincronização entre carousel principal e thumbnails
 */
function setupCarouselSync(instances) {
  const mainCarousel = instances.get("productMainCarousel");
  const thumbsCarousel = instances.get("productThumbsCarousel");

  if (!mainCarousel || !thumbsCarousel) return;

  // 🏷️ Marca o thumb correspondente ao índice ativo do carousel principal
  const setActiveThumb = (index) => {
    thumbsCarousel.slides.forEach((slide, i) => {
      slide.classList.toggle("swiper-slide-thumb-active", i === index);
    });
  };

  // Estado inicial sincronizado
  setActiveThumb(mainCarousel.activeIndex);
  thumbsCarousel.slideTo(mainCarousel.activeIndex, 0);

  // 🔗 Main -> Thumbs (todas as resoluções)
  mainCarousel.on("slideChange", function () {
    const activeIndex = this.activeIndex;
    thumbsCarousel.slideTo(activeIndex);
    setActiveThumb(activeIndex);
  });

  // 🔗 Thumbs -> Main (clique nos thumbs, todas as resoluções)
  thumbsCarousel.slides.forEach((slide, index) => {
    slide.addEventListener("click", function (e) {
      // Evita reproduzir vídeo ao clicar num thumb que contenha iframe
      if (slide.querySelector("iframe")) {
        e.preventDefault();
        e.stopPropagation();
      }

      mainCarousel.slideTo(index);
    });
  });

  // Ajuste de altura para orientação vertical (apenas desktop)
  const isMobile = window.innerWidth < 1024;
  const thumbsElement = document.querySelector(".productThumbsCarousel");
  if (
    thumbsElement &&
    thumbsElement.dataset.direction === "vertical" &&
    !isMobile
  ) {
    const updateThumbsHeight = () => {
      const mainElement =
        document.querySelector(
          ".productMainCarousel .swiper-zoom-container:first-child img",
        ) ||
        document.querySelector(".productMainCarousel .swiper-slide-active img");
      if (mainElement) {
        const mainHeight = mainElement.offsetHeight;
        const container = thumbsElement.closest(
          ".product-swiper-thumbs.vertical",
        );
        if (container && mainHeight > 0) {
           container.style.setProperty('--product-thumbs-height', `${mainHeight}px`);
        }
      }
    };

    setTimeout(updateThumbsHeight, 100);
    window.addEventListener("resize", updateThumbsHeight);
  }

  console.log("✅ Sincronização de carousels de produto configurada");
}

if (typeof window !== "undefined") {
  window.initCarousels = initPageCarousels;
  window.ensureIrrobaSwiper = ensureSwiperScript;

  window.addEventListener("message", function (event) {
    const { action, selector, options, forceReinit } = event.data || {};

    if (action === "initCarousels") {
      initPageCarousels(selector, options, forceReinit);
    }
  });
}
